document.documentElement.classList.remove('no-js');

function spellboundFormatMoney(cents, currencyCode) {
  const amount = Number(cents) / 100;
  if (currencyCode && typeof Intl !== 'undefined') {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).format(amount);
    } catch {
      /* fall through */
    }
  }
  return amount.toFixed(2);
}

function spellboundFindVariant(variants, selectedOptions) {
  return variants.find((v) => selectedOptions.every((val, i) => v[`option${i + 1}`] === val));
}

function spellboundInitProductVariantPicker() {
  document.querySelectorAll('.product-page .product-form').forEach((form) => {
    const root = form.closest('[data-product-root]');
    const page = form.closest('.product-page');
    const configEl = page?.querySelector('[data-product-config]');
    const variantPicker = form.querySelector('.variant-picker');
    if (!configEl || !variantPicker) return;

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch {
      return;
    }

    const { variants, currency } = config;
    const idInput = form.querySelector('input[name="id"]');
    const priceEl = root?.querySelector('[data-product-price]');
    if (!idInput || !priceEl || !Array.isArray(variants)) return;

    function applySelection() {
      const selects = variantPicker.querySelectorAll('select[name^="options"]');
      const selected = Array.from(selects).map((s) => s.value);
      const variant = spellboundFindVariant(variants, selected);
      if (!variant) return;

      idInput.value = String(variant.id);

      let html = spellboundFormatMoney(variant.price, currency);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        html += ` <s>${spellboundFormatMoney(variant.compare_at_price, currency)}</s>`;
      }
      priceEl.innerHTML = html;
    }

    variantPicker.querySelectorAll('select').forEach((sel) => {
      sel.addEventListener('change', applySelection);
    });
    applySelection();
  });
}

function spellboundApplyCartResponse(cart, currencyCode) {
  if (!cart || !Array.isArray(cart.items)) return;
  document.querySelectorAll('.cart-item[data-line-key]').forEach((row) => {
    const key = row.getAttribute('data-line-key');
    const item = cart.items.find((i) => i.key === key);
    const priceEl = row.querySelector('[data-line-price]');
    if (!item) {
      row.remove();
    } else if (priceEl) {
      priceEl.textContent = spellboundFormatMoney(item.final_line_price, currencyCode);
    }
  });
  const totalEl = document.querySelector('.cart-summary__total');
  if (totalEl) {
    totalEl.textContent = spellboundFormatMoney(cart.total_price, currencyCode);
  }
  const countEl = document.querySelector('.cart-count');
  if (countEl) {
    countEl.textContent = String(cart.item_count);
  }
}

function spellboundOptimisticLinePrice(form, row, nextQty, currencyCode) {
  const priceEl = row.querySelector('[data-line-price]');
  if (!priceEl) return;
  const lineCents = parseInt(row.getAttribute('data-line-cents'), 10) || 0;
  const baseQty = parseInt(row.getAttribute('data-line-qty'), 10) || 1;
  if (nextQty <= 0) return;
  const unit = Math.round(lineCents / Math.max(1, baseQty));
  const nextLineCents = unit * nextQty;
  priceEl.textContent = spellboundFormatMoney(nextLineCents, currencyCode);
  if (form.querySelectorAll('.cart-item[data-line-key]').length === 1) {
    const totalEl = document.querySelector('.cart-summary__total');
    if (totalEl) {
      totalEl.textContent = spellboundFormatMoney(nextLineCents, currencyCode);
    }
  }
}

function spellboundInitCartForm() {
  const form = document.querySelector('[data-cart-form]');
  if (!form) return;

  const currencyCode = form.getAttribute('data-cart-currency') || '';

  form.querySelectorAll('[data-qty-stepper]').forEach((stepper) => {
    const line = parseInt(stepper.getAttribute('data-line'), 10);
    if (Number.isNaN(line)) return;

    const row = stepper.closest('.cart-item');
    const input = stepper.querySelector('.qty-stepper__input');
    const hidden = row?.querySelector('.cart-item__hidden-qty');
    if (!row || !input || !hidden) return;

    let abortCtrl = null;

    stepper.querySelectorAll('.qty-stepper__btn').forEach((btn) => {
      btn.addEventListener('mousedown', (e) => {
        e.preventDefault();
      });
    });

    async function pushQuantity(nextQty, opts = { optimistic: false }) {
      const qty = Math.max(0, Math.floor(Number(nextQty)));
      const prevCount = form.querySelectorAll('.cart-item[data-line-key]').length;

      if (opts.optimistic && qty > 0) {
        requestAnimationFrame(() => {
          spellboundOptimisticLinePrice(form, row, qty, currencyCode);
        });
      }

      abortCtrl?.abort();
      abortCtrl = new AbortController();
      const { signal } = abortCtrl;

      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity: qty }),
          signal,
        });
        const cart = await res.json();
        if (!res.ok) {
          throw new Error(cart.description || cart.message || 'Cart update failed');
        }

        hidden.value = String(qty);
        input.value = String(qty);

        const item = cart.items.find((i) => i.key === row.getAttribute('data-line-key'));
        if (item) {
          row.setAttribute('data-line-cents', String(item.final_line_price));
          row.setAttribute('data-line-qty', String(item.quantity));
        }

        if (qty === 0 || cart.items.length !== prevCount) {
          window.location.reload();
          return;
        }

        spellboundApplyCartResponse(cart, currencyCode);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error(err);
        input.value = hidden.value;
        try {
          const recover = await fetch('/cart.js').then((r) => r.json());
          spellboundApplyCartResponse(recover, currencyCode);
        } catch {
          window.location.reload();
        }
      }
    }

    stepper.querySelector('[data-qty-dec]')?.addEventListener('click', () => {
      const v = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
      input.value = String(v);
      void pushQuantity(v, { optimistic: true });
    });

    stepper.querySelector('[data-qty-inc]')?.addEventListener('click', () => {
      const v = Math.max(0, (parseInt(input.value, 10) || 0) + 1);
      input.value = String(v);
      void pushQuantity(v, { optimistic: true });
    });

    input.addEventListener('blur', () => {
      let v = parseInt(input.value, 10);
      if (Number.isNaN(v) || v < 0) v = 0;
      input.value = String(v);
      const prev = parseInt(hidden.value, 10) || 0;
      if (v !== prev) {
        void pushQuantity(v, { optimistic: false });
      }
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        input.blur();
      }
    });
  });
}

function spellboundOnReady() {
  spellboundInitProductVariantPicker();
  spellboundInitCartForm();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', spellboundOnReady);
} else {
  spellboundOnReady();
}

document.addEventListener('click', (event) => {
  const menuButton = event.target.closest('[data-menu-toggle]');
  if (menuButton) {
    const nav = document.querySelector('[data-site-nav]');
    const expanded = menuButton.getAttribute('aria-expanded') === 'true';
    menuButton.setAttribute('aria-expanded', String(!expanded));
    nav?.classList.toggle('is-open', !expanded);
  }

  const thumb = event.target.closest('[data-product-thumb]');
  if (thumb) {
    const main = document.querySelector('[data-product-main-image]');
    const src = thumb.getAttribute('data-product-thumb');
    const alt = thumb.getAttribute('data-product-alt') || '';
    if (main && src) {
      main.setAttribute('src', src);
      main.setAttribute('alt', alt);
    }
  }
});
